import { useState } from 'react';
import { Eye, EyeOff, Lock, Mail, KeyRound, Loader2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

interface AdminLoginProps {
  onLoginSuccess: () => void;
}

export default function AdminLogin({ onLoginSuccess }: AdminLoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    setLoading(true);

    // Add a premium feeling artificial delay (1.2 seconds) to simulate authentication verification
    setTimeout(() => {
      if (email.trim() === 'zahra@admin.com' && password === 'Admin1985#') {
        localStorage.setItem('admin_authenticated', 'true');
        toast.success('تم تسجيل الدخول بنجاح! مرحباً بك');
        onLoginSuccess();
      } else {
        toast.error('البريد الإلكتروني أو كلمة المرور غير صحيحة');
        setLoading(false);
      }
    }, 1200);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-radial from-[#1e1a0b] via-[#0f0e0a] to-[#050504] px-4 py-12 relative overflow-hidden" dir="rtl">
      {/* Decorative Golden Ambient Lights */}
      <div className="absolute top-[-20%] left-[-20%] w-[60%] h-[60%] rounded-full bg-[#D4AF37]/10 blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-[-20%] right-[-20%] w-[60%] h-[60%] rounded-full bg-[#8B7109]/15 blur-[120px] pointer-events-none"></div>

      <div className="w-full max-w-md z-10 transition-all duration-300 hover:scale-[1.01]">
        {/* Logo / Title Area */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8B7109] text-white shadow-xl shadow-[#D4AF37]/20 mb-4 ring-4 ring-[#D4AF37]/10">
            <KeyRound className="w-8 h-8" />
          </div>
          <h2 className="text-3xl font-extrabold text-white tracking-tight">
            <span className="text-[#D4AF37]">دار</span> العقار
          </h2>
          <p className="text-gray-400 mt-2 text-sm font-medium">لوحة تحكم المسؤول للنظام العقاري</p>
        </div>

        {/* Login Card */}
        <Card className="border-[#D4AF37]/20 bg-black/40 backdrop-blur-xl shadow-2xl relative">
          {/* Card subtle gold highlight border top */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent"></div>
          
          <CardHeader className="space-y-1 pb-6 text-center">
            <CardTitle className="text-2xl font-bold text-white">تسجيل الدخول</CardTitle>
            <CardDescription className="text-gray-400">
              أدخل بيانات الاعتماد الخاصة بك للوصول إلى لوحة التحكم
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Input */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-300">
                  البريد الإلكتروني
                </Label>
                <div className="relative">
                  <span className="absolute inset-y-0 right-3 flex items-center text-gray-500 pointer-events-none">
                    <Mail className="w-4 h-4" />
                  </span>
                  <Input
                    id="email"
                    type="email"
                    placeholder="example@admin.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    className="pr-10 bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-[#D4AF37] focus:ring-[#D4AF37]/50"
                    required
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-300">
                  كلمة المرور
                </Label>
                <div className="relative">
                  <span className="absolute inset-y-0 right-3 flex items-center text-gray-500 pointer-events-none">
                    <Lock className="w-4 h-4" />
                  </span>
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    className="pr-10 pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-[#D4AF37] focus:ring-[#D4AF37]/50"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                    className="absolute inset-y-0 left-3 flex items-center text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full mt-2 py-6 bg-gradient-to-r from-[#D4AF37] to-[#8B7109] hover:from-[#e5bf3f] hover:to-[#9c7f0b] text-black font-bold text-base transition-all duration-300 shadow-lg shadow-[#D4AF37]/15 rounded-lg active:scale-[0.98]"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 ml-2 animate-spin" />
                    جاري التحقق من البيانات...
                  </>
                ) : (
                  'تسجيل الدخول'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer info / Back link */}
        <div className="text-center mt-6">
          <a
            href="/"
            className="text-sm text-gray-500 hover:text-[#D4AF37] transition-colors duration-200"
          >
            العودة إلى الموقع الرئيسي
          </a>
        </div>
      </div>
    </div>
  );
}
